import dayjs from 'dayjs';
import {
  Maybe,
  RequestSchedulePreference,
  SchedulePreference,
} from '../graphql/schema';
import {
  DateRangeMode,
  DatesAvailabilityType,
  DateWorkingMode,
  TestDriveDay,
} from '../models/entities/calendar.model';
import { TimeSlotString } from '../models/entities/timeslot.model';
import { i18n } from '../services/translation.service';
import UtilsService from './utils.service';

import DateTimeFormatOptions = Intl.DateTimeFormatOptions;
export default class DateService {
  /**
   * In this method we calculate the endDate passed to our BE to fetch
   * The corresponding calendars and vehicles that has booking.
   * Normally we request one month at a time and thats why the default page is 1.
   * For this we have two constants one that represents the maximum number available for a month(31)
   * And other to fetch a few more days of the next month
   * in case the user clicks already in a day of the next month in the calendar.
   * @param periodStartDate
   * @param page Number of months we want to request to BE
   * @returns The calculated endDate based on pages
   */
  static addMonths(periodStartDate: Date, page = 1): Date {
    const TOTALNUMBEROFDAYSINMONTH = 31;
    const DATETHRESHOLD = 7;
    const dateOffset = TOTALNUMBEROFDAYSINMONTH * page + DATETHRESHOLD;
    const newDate = new Date(periodStartDate);
    newDate.setDate(periodStartDate.getDate() + dateOffset);
    return newDate;
  }

  // this method is necessary to build a date with a correct UTC value
  // if we do setDate(), it includes midnight of the timezone you are in.
  // Sun Oct 31 2021 00:00:00 GMT+0100
  // when converting toISOString(), it formats the date int the wrong format:
  // '2021-10-30T23:00:00.000Z'
  // this method guarantees that the date is always in the UTC correct format
  static buildDate(date: Date): Date {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12)
    );
  }

  static buildPeriodLabel(
    option: any,
    bookingShown: boolean | undefined
  ): string {
    let label = '';
    if (bookingShown && option && option.label) {
      // eslint-disable-next-line prefer-destructuring
      label = option.label;
    } else if (option && option.periodId) {
      label = i18n.t(`schedule.period.${option.periodId}`) as string;
    }
    return label;
  }

  static requestTimeSlotFormat(period: any): any {
    return {
      text: `${i18n.t(`schedule.period.${period.periodId}`)} <span>${i18n.t(
        `schedule.period.${period.periodId}.times`
      )}</span>`,
      value: {
        start: period.start,
        end: period.end,
        dayPeriod: period.periodId,
        label: i18n.t(`schedule.period.${period.periodId}.times`),
      },
    };
  }

  static getSelectedPeriodSummary(
    selectedDay: Date | undefined,
    selectedTimeFrame: string,
    locale: string
  ): string {
    let retVal = '';
    if (selectedDay) {
      let weekday = new Intl.DateTimeFormat(locale, {
        weekday: 'long',
      }).format(selectedDay);

      weekday = `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}`;
      const date = DateService.formatDate(locale, selectedDay, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
      const formattedDate = i18n.t('summary.weekdayAndDate.template', {
        date,
        weekday,
      });

      retVal = `${formattedDate}${
        selectedTimeFrame ? ',' : ''
      } ${selectedTimeFrame}`;
    }

    return retVal;
  }

  static hasInvalidDatesIntoRange(
    date: Date,
    availableDates: TestDriveDay[],
    selectedRangeDate: DateRangeMode,
    datePickerOrder?: DateWorkingMode
  ): boolean {
    if (datePickerOrder === DateWorkingMode.START) {
      return availableDates.some((item: TestDriveDay) => {
        const checkedDate = new Date(item.date);
        return (
          !item.available &&
          DateService.isGreaterThan(checkedDate, date) &&
          DateService.isGreaterThan(selectedRangeDate.end, checkedDate)
        );
      });
    }
    return availableDates.some((item: TestDriveDay) => {
      const checkedDate = new Date(item.date);
      return (
        !item.available &&
        DateService.isGreaterThan(checkedDate, selectedRangeDate.start) &&
        DateService.isGreaterThan(date, checkedDate)
      );
    });
  }

  static inBetweenDates = (
    currentDate: Date,
    { startDate, endDate }: { startDate?: Date; endDate?: Date }
  ): boolean => {
    if (!UtilsService.isDateValid(currentDate)) return false;

    const beginDate = startDate || currentDate;
    const finishDate = endDate || currentDate;

    return +beginDate < +currentDate && +finishDate > +currentDate;
  };

  static isGreaterThan = (firstDate?: Date, secondDate?: Date): boolean => {
    if (!firstDate || !secondDate) return false;
    if (!UtilsService.isDateValid(firstDate)) return false;
    if (!UtilsService.isDateValid(secondDate)) return false;

    return firstDate > secondDate;
  };

  /**
   * Given two Dates checks whether they are equal or not
   * @param firstDate
   * @param secondDate
   */
  static isVBSDateEqual(firstDate?: Date, secondDate?: Date): boolean {
    if (
      !UtilsService.isDateValid(new Date(firstDate || '')) ||
      !UtilsService.isDateValid(new Date(secondDate || ''))
    )
      return false;
    return (
      DateService.formatDateForVBS(firstDate) ===
      DateService.formatDateForVBS(secondDate)
    );
  }

  static firstDayOfMonth(dateObject?: {
    date?: Date;
    year?: number;
    month?: number;
  }): Date {
    // if no date is passed, we assume a new date (today)
    const newDate = dateObject?.date || new Date();
    return new Date(
      Date.UTC(
        dateObject?.year || newDate.getFullYear(),
        dateObject?.month || newDate.getMonth(),
        1,
        dateObject?.date ? 0 : 12
      )
    );
  }

  static formatDate(
    locale: string,
    date: Date,
    options?: DateTimeFormatOptions
  ): string {
    return DateService.getDateTimeFormatter(locale, options).format(date);
  }

  static formatDateForVBS(date?: Date): string {
    return date?.toISOString().slice(0, 10) || '';
  }

  static formatDateRange(date?: SchedulePreference): TimeSlotString {
    return {
      start: date?.pickUpDateTime,
      end: date?.returnDateTime,
    };
  }

  static formatUTCDate(locale: string, day: Date): string {
    return DateService.formatDate(locale, day, { day: '2-digit' });
  }

  static getAvailableDates(calendar: Maybe<TestDriveDay[]>): Maybe<string[]> {
    if (calendar) {
      return calendar
        .map((day: any) => day.available && day.date)
        .filter(Boolean);
    }
    return [];
  }

  static getClosedDates(calendar: Maybe<TestDriveDay[]>): Maybe<string[]> {
    let closedDates;
    if (calendar) {
      closedDates = calendar
        .map((day) => !day.dealerOpen && day.date)
        .filter(Boolean);
    }
    return closedDates as Maybe<string[]>;
  }

  static getDateFromStringWithOffset(dateString: string): Date {
    // remove either positive or negative offsets
    let [date] = dateString.split(/([+-]\d{2}:\d{2}|Z)/);
    date = `${date}+00:00`;
    return new Date(date);
  }

  static getDatesAvailability(
    calendar: Maybe<TestDriveDay[]>
  ): DatesAvailabilityType {
    let availableDates;
    let closedDates;
    if (calendar) {
      availableDates = DateService.getAvailableDates(calendar);
      closedDates = DateService.getClosedDates(calendar);
    }
    return { availableDates, closedDates } as DatesAvailabilityType;
  }

  static getDateTimeFormatter(
    locale: string,
    options: DateTimeFormatOptions = {}
  ): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat(
      locale,
      Object.assign({}, options, { timeZone: 'UTC' })
    );
  }

  static getMinToHours(min: number): number {
    return min / 60;
  }

  static getShortMonth(locale: string, day: Date): string {
    return DateService.formatDate(locale, day, { month: 'short' });
  }

  static getTestDriveDateString(
    schedule: SchedulePreference,
    locale: string
  ): string {
    const offsetCompensatedDate = DateService.getOffsetCompensatedDate(
      schedule.pickUpDateTime,
      locale
    );
    const date = DateService.getDateFromStringWithOffset(
      schedule.pickUpDateTime
    );
    return `${UtilsService.capitalizeFirstLetter(
      dayjs(date).format('dddd')
    )}, ${offsetCompensatedDate}`;
  }

  static getTestDriveTimeString(schedule: SchedulePreference): string {
    return `${dayjs(schedule.pickUpDateTime).format('HH:mm')} - ${dayjs(
      schedule.returnDateTime
    ).format('HH:mm')}`;
  }

  static getCompleteTime(schedule: SchedulePreference, locale: string): string {
    return `${this.getOffsetCompensatedTime(
      schedule.pickUpDateTime,
      locale
    )} - ${this.getOffsetCompensatedTime(schedule.returnDateTime, locale)}`;
  }

  static getOffsetCompensatedTime(schedule: string, locale: string): string {
    const date = DateService.getDateFromStringWithOffset(schedule);
    return DateService.getDateTimeFormatter(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
  static getOffsetCompensatedDate(schedule: string, locale: string): string {
    const date = DateService.getDateFromStringWithOffset(schedule);
    return DateService.getDateTimeFormatter(locale).format(date);
  }

  static getTestDrivePeriodString(schedule: RequestSchedulePreference): string {
    return schedule.dayPeriod
      ? `${schedule.dayPeriod
          .charAt(0)
          .toUpperCase()}${schedule.dayPeriod.slice(1)}`
      : '';
  }

  static getTime(dateString: string, locale: string): string {
    const date = DateService.getDateFromStringWithOffset(dateString);
    return DateService.getDateTimeFormatter(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  /**
   * Resets hours and returns a date timestamp without the time
   * @param date Reset
   * @returns Number timespamt
   */
  static resetHours(date: Date | string): number {
    return new Date(date).setHours(0, 0, 0, 0);
  }

  static toIsoStringShort(date: Date): string {
    return date.toISOString().slice(0, 10);
  }
}
